import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../contexts/AuthContext";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import {
  ticketsAPI,
  usersAPI,
  categoriesAPI,
  autoResponsesAPI,
  clientsAPI,
  welcomeQuestionsAPI,
  procedureRecordingsAPI,
} from "../services/api";
import {
  Send,
  Paperclip,
  Mic,
  ArrowLeft,
  Phone,
  Clock,
  RefreshCw,
  UserPlus,
  CheckCircle,
  Zap,
  Pencil,
  Trash2,
  X,
  Check,
  CheckCheck,
  Reply,
  Inbox,
  ChevronUp,
  ChevronDown,
  Edit3,
  Search,
  Loader2,
  Building2,
  UserIcon,
  MessageCircle,
  Archive,
  ChevronLeft,
  ChevronRight,
  MessagesSquare,
  Calendar,
  ZoomIn,
  ZoomOut,
  Download,
  Eye,
  FileVideo,
} from "lucide-react";
import { formatCNPJ, formatWhatsAppNumber } from "@/utils/formatters";
import ProcedureRecorderDialog from "../components/ProcedureRecorderDialog";
import ProcedureRecordingsList from "../components/ProcedureRecordingsList";
import ProcedureRecordingPickerDialog from "../components/ProcedureRecordingPickerDialog";
import ClosePotentialTicketButton from "../components/ClosePotentialTicketButton";
import VoiceRecordingControls from "../components/VoiceRecordingControls";
import { getClosureToast, getTicketErrorMessage } from "../utils/ticketErrors";
import ClientNameChoiceDialog from "../components/ClientNameChoiceDialog";
import { getExternalClientName } from "../utils/clientNames";

const renderStatus = (msg) => {
  if (msg.is_from_client) return null;

  switch (msg.status) {
    case "failed":
      return <X className="w-3 h-3 text-red-500" />;
    case "sent":
      return <Check className="w-3 h-3 text-gray-500" />;
    case "delivered":
      return <CheckCheck className="w-3 h-3 text-gray-500" />;
    case "read":
      return <CheckCheck className="w-3 h-3 text-cyan-500" />;
    default:
      return <Clock className="w-3 h-3 text-gray-500 opacity-50" />;
  }
};

const getMessageRevision = (msg) => {
  const attachment = msg.attachment || {};

  return [
    msg.id,
    msg.message,
    msg.status,
    msg.wa_message_id,
    msg.is_edited,
    msg.is_deleted,
    msg.reaction,
    msg.quoted_message_id,
    attachment.file_id,
    attachment.filename,
    attachment.content_type,
    attachment.url,
  ].join("|");
};

const Chat = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const messageScope = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("view") === "attendance" ? "ticket" : "client";
  }, [location.search]);

  const [myActiveTickets, setMyActiveTickets] = useState([]);
  const [pendingTickets, setPendingTickets] = useState([]);
  const [potentialTickets, setPotentialTickets] = useState([]);
  const [activeSidebarTab, setActiveSidebarTab] = useState("meus");
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendingAudio, setSendingAudio] = useState(false);
  const [sendingFile, setSendingFile] = useState(false);
  const [users, setUsers] = useState([]);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [showClosureDetailsDialog, setShowClosureDetailsDialog] = useState(false);
  const [showProcedureRecordings, setShowProcedureRecordings] = useState(false);
  const [showProcedurePicker, setShowProcedurePicker] = useState(false);
  const [procedureRecordingsRefreshKey, setProcedureRecordingsRefreshKey] = useState(0);
  const [procedureRecordingsCount, setProcedureRecordingsCount] = useState(0);
  const [transferReason, setTransferReason] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [showDepartmentPrompt, setShowDepartmentPrompt] = useState(false);
  const [requiredDepartment, setRequiredDepartment] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isResolving, setIsResolving] = useState(false);
  const resolvingRef = useRef(false);
  const completedClosureRef = useRef(null);
  const activeTicketIdRef = useRef(ticketId);
  activeTicketIdRef.current = ticketId;
  const [isDragging, setIsDragging] = useState(false);
  const [isProfilePicOpen, setIsProfilePicOpen] = useState(false);
  const [headerExpanded, setHeaderExpanded] = useState(() => {
    const saved = localStorage.getItem("chatHeaderExpanded");
    return saved !== null ? JSON.parse(saved) : false;
  });

  const toggleHeader = () => {
    setHeaderExpanded((prev) => {
      const newState = !prev;
      localStorage.setItem("chatHeaderExpanded", JSON.stringify(newState));
      return newState;
    });
  };

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const [isPreparingAudio, setIsPreparingAudio] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const recordingElapsedRef = useRef(0);
  const recordingStartedAtRef = useRef(null);
  const recordingRequestRef = useRef(null);
  const audioPreviewRef = useRef(null);
  const audioPreviewUrlRef = useRef(null);
  const finishRecordingRef = useRef(null);
  const audioSendInFlightRef = useRef(false);

  const [quickReplies, setQuickReplies] = useState([]);
  const [filteredReplies, setFilteredReplies] = useState([]);
  const [showQuickMenu, setShowQuickMenu] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const editInputRef = useRef(null);

  const isAssignedToMe = ticket?.assigned_to === user?.id;
  const canMonitorSupport = ["analyst", "admin"].includes(user?.role);
  const isSpectator =
    ticket?.status === "in_progress" &&
    !isAssignedToMe &&
    canMonitorSupport;
  const canManageTicket = ["pending", "in_progress"].includes(ticket?.status);
  const isFinalizedTicket = ["resolved", "closed"].includes(ticket?.status);
  const isPotentialTicket = ticket?.status === "pending" && !ticket?.menu_response_received;
  const canReply = ticket?.status === "in_progress" && isAssignedToMe;
  const canEditContact =
    ticket?.status === "pending" || canReply || user?.role === "admin";
  const canModifyTicketConversation = canReply || user?.role === "admin";

  useEffect(() => {
    setRecordedAudio(null);
    setIsStartingRecording(false);
    setIsPreparingAudio(false);
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);

    return () => {
      recordingRequestRef.current = null;
      const recorder = mediaRecorderRef.current;
      if (recorder) {
        recorder.onstop = null;
        recorder.ondataavailable = null;
        if (recorder.state !== "inactive") recorder.stop();
        recorder.stream.getTracks().forEach((track) => track.stop());
        mediaRecorderRef.current = null;
      }
      audioChunksRef.current = [];
      clearInterval(timerRef.current);
      recordingElapsedRef.current = 0;
      recordingStartedAtRef.current = null;
      finishRecordingRef.current?.(null);
      finishRecordingRef.current = null;
      if (audioPreviewUrlRef.current) {
        URL.revokeObjectURL(audioPreviewUrlRef.current);
        audioPreviewUrlRef.current = null;
      }
    };
  }, [ticketId, canReply]);

  useEffect(() => {
    let cancelled = false;

    const loadProcedureRecordingsCount = async () => {
      if (!ticket?.id) {
        setProcedureRecordingsCount(0);
        return;
      }

      try {
        const response = await procedureRecordingsAPI.count(ticket.id);
        if (!cancelled) setProcedureRecordingsCount(response.data?.count || 0);
      } catch (error) {
        console.error("Erro ao carregar quantidade de procedimentos", error);
        if (!cancelled) setProcedureRecordingsCount(0);
      }
    };

    loadProcedureRecordingsCount();
    return () => {
      cancelled = true;
    };
  }, [ticket?.id, procedureRecordingsRefreshKey]);

  const sendSelectedProcedureRecordings = async (recordingIds) => {
    try {
      const response = await procedureRecordingsAPI.sendToTicket(ticket.id, recordingIds);
      await loadMessages();
      toast({
        title: "Procedimento enviado",
        description: response.data?.message || "Os vídeos foram enviados ao cliente.",
      });
    } catch (error) {
      const message = error?.response?.data?.detail || "Não foi possível enviar os procedimentos selecionados.";
      toast({ title: "Não foi possível enviar", description: message, variant: "destructive" });
      throw error;
    }
  };

  const [resolveData, setResolveData] = useState({
    category: "",
    description: "",
    status: "resolved",
  });

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);
  const activeTicketsLoadingRef = useRef(false);
  const currentTicketLoadingRef = useRef(false);
  const messagesLoadingRef = useRef(false);
  const lastMarkAsReadAtRef = useRef(0);
  const redirectTimeoutRef = useRef(null);
  const chatContainerRef = useRef(null);
  const isUserScrolledUp = useRef(false);
  const messageInputRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [unreadInChat, setUnreadInChat] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const messageLimitRef = useRef(50);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingOldMessagesRef = useRef(false);
  const lastMessageIdRef = useRef(null);
  const ignoreScrollEventsRef = useRef(false);
  const audioRefs = useRef({});

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("chatSidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    if (window.innerWidth < 768) {
      setIsMobileSidebarOpen(!isMobileSidebarOpen);
    } else {
      setIsSidebarCollapsed((prev) => {
        const newState = !prev;
        localStorage.setItem("chatSidebarCollapsed", JSON.stringify(newState));
        return newState;
      });
    }
  };

  const [replyingTo, setReplyingTo] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape" && zoomedImage) {
        setZoomedImage(null);
        setZoomScale(1);
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [zoomedImage]);

  const handleDownload = async (e, url, filename) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      toast({ title: "Iniciando download...", description: filename });
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Erro ao baixar:", error);
      toast({
        title: "Erro",
        description: "O download bloqueou. Abrindo arquivo...",
        variant: "destructive",
      });
      window.open(url, "_blank");
    }
  };

  const [showEditClientDialog, setShowEditClientDialog] = useState(false);
  const [pendingClientSelection, setPendingClientSelection] = useState(null);
  const [isSavingClient, setIsSavingClient] = useState(false);
  const [clientEditData, setClientEditData] = useState({
    name: "",
    real_phone: "",
    company: "",
    external_client_id: "",
    cnpj: "",
    full_data: null,
  });
  const [externalClients, setExternalClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [loadingClients, setLoadingClients] = useState(false);

  const filteredClients = externalClients.filter((c) => {
    if (!clientSearch) return false;

    const term = String(clientSearch).toLowerCase();

    return (
      String(c.name || "")
        .toLowerCase()
        .includes(term) ||
      String(c.company || "")
        .toLowerCase()
        .includes(term) ||
      String(c.razao_social || "")
        .toLowerCase()
        .includes(term) ||
      String(c.nome_fantasia || "")
        .toLowerCase()
        .includes(term) ||
      String(c.cnpj || "").includes(term) ||
      String(c.whatsapp || c.phone || "").includes(term)
    );
  });

  const loadExternalClientsForEdit = async () => {
    setLoadingClients(true);
    try {
      const response = await clientsAPI.getExternal();
      const clientsList = response.data?.clients || [];
      setExternalClients(clientsList);
    } catch (error) {
      console.error("Erro ao carregar clientes externos", error);
      setExternalClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleFileCapture = (file) => {
    if (!file) return;

    const fakeEvent = {
      target: {
        files: [file],
      },
    };
    handleFileUpload(fakeEvent);
  };

  const handleInputPaste = (e) => {
    if (
      e.clipboardData &&
      e.clipboardData.files &&
      e.clipboardData.files.length > 0
    ) {
      e.preventDefault();
      const file = e.clipboardData.files[0];
      handleFileCapture(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (ticket?.status === "in_progress") {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (ticket?.status !== "in_progress") return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleFileCapture(file);
    }
  };

  const handleReplyClick = (msg) => {
    setReplyingTo(msg);
    setTimeout(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }, 50);
  };

  const handleOpenEditClient = () => {
    setClientEditData({
      name: ticket.client_name || "",
      real_phone: ticket.external_client_data?.real_phone || "",
      company: ticket.external_client_company || "",
      external_client_id: ticket.external_client_id || "",
      cnpj: ticket.external_client_data?.cnpj || "",
      full_data: ticket.external_client_data || null,
    });
    setClientSearch("");
    setShowEditClientDialog(true);
    loadExternalClientsForEdit();
  };

  const applySelectedClient = (clientData, updateClientName = false) => {
    const apiPhone = clientData.whatsapp || clientData.phone || "";
    const currentPhone = clientEditData.real_phone;

    let newData = {
      ...clientEditData,
      name: updateClientName ? getExternalClientName(clientData) : clientEditData.name,
      company: getExternalClientName(clientData) || clientData.company || "",
      external_client_id: clientData.id || clientData.codigo || "",
      cnpj: clientData.cnpj || "",
      full_data: clientData.raw_data || clientData,
    };

    if (apiPhone) {
      if (!currentPhone) {
        newData.real_phone = apiPhone;
        toast({
          title: "📱 Telefone Preenchido",
          description: `O número ${apiPhone} da API foi adicionado.`,
        });
      } else if (currentPhone !== apiPhone) {
        toast({
          title: "⚠️ Divergência de Telefone",
          description: `A API possui o número: ${apiPhone}.`,
          duration: 6000,
          action: (
            <button
              onClick={() =>
                setClientEditData((prev) => ({ ...prev, real_phone: apiPhone }))
              }
              className="bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1.5 text-xs font-semibold rounded hover:bg-blue-200 transition-colors shrink-0"
            >
              Sobrescrever
            </button>
          ),
        });
      }
    }

    setClientEditData(newData);
    setClientSearch("");
  };

  const handleSelectClientFromApi = (clientData) => {
    const companyName = getExternalClientName(clientData);
    if (companyName && companyName !== (clientEditData.name || "").trim()) {
      setPendingClientSelection({ clientData, currentName: clientEditData.name, companyName });
      return;
    }
    applySelectedClient(clientData);
  };

  const handleSaveClient = async () => {
    setIsSavingClient(true);
    try {
      await clientsAPI.updateLocal(ticket.client_phone, clientEditData);

      if (clientEditData.external_client_id) {
        const payload = {
          external_client_id: String(clientEditData.external_client_id),
          update_client_name: false,
          external_client_data: {
            ...clientEditData.full_data,
            real_phone: clientEditData.real_phone,
          },
        };
        await ticketsAPI.linkClient(ticket.id, payload);
      } else {
        if (ticketsAPI.unlinkClient) {
          await ticketsAPI.unlinkClient(ticket.id);
        }
      }

      toast({
        title: "Contato Atualizado!",
        description: "Os dados foram salvos com sucesso.",
      });

      setShowEditClientDialog(false);
      loadTicket();
    } catch (error) {
      console.error("Erro ao salvar contato:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o contato.",
        variant: "destructive",
      });
    } finally {
      setIsSavingClient(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadDepartments();
    loadCategories();
    loadQuickReplies();
    completedClosureRef.current = null;

    if (!ticketId) {
      setLoading(false);
      setTicket(null);
      return;
    }

    setLoading(true);
    messageLimitRef.current = 50;
    setMessages([]);
    setHasMoreMessages(false);
    setUnreadInChat(0);
    setIsScrolledUp(false);
    isUserScrolledUp.current = false;
    prevMessagesLengthRef.current = 0;
    lastMessageIdRef.current = null;

    const refreshCurrentTicket = async () => {
      if (currentTicketLoadingRef.current || resolvingRef.current) return;
      currentTicketLoadingRef.current = true;

      try {
        await Promise.all([loadMessages(), loadTicket()]);
        sendHeartbeat();
      } finally {
        currentTicketLoadingRef.current = false;
      }
    };

    refreshCurrentTicket();
    pollingRef.current = setInterval(refreshCurrentTicket, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [ticketId, messageScope]);

  useEffect(() => {
    if (ticket && user) {
      if (
        ticket.status === "in_progress" &&
        ticket.assigned_to &&
        ticket.assigned_to !== user.id &&
        !canMonitorSupport
      ) {
        if (redirectTimeoutRef.current) return;
        redirectTimeoutRef.current = setTimeout(() => {
          toast({
          title: "Atendimento Assumido! 🏃💨",
          description: ticket.assigned_to_name
            ? `O atendente ${ticket.assigned_to_name} assumiu este atendimento.`
            : "Outro atendente assumiu este atendimento.",
          variant: "destructive",
          duration: 5000,
        });

          navigate("/chat");
          redirectTimeoutRef.current = null;
        }, 3000);
      }
    }
  }, [ticket, user, navigate, toast]);

  useEffect(() => {
    const shouldStayOnTicket =
      !ticket ||
      !user ||
      ticket.status !== "in_progress" ||
      !ticket.assigned_to ||
      ticket.assigned_to === user.id ||
      canMonitorSupport;

    if (shouldStayOnTicket && redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }
  }, [ticket, user]);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const handleSaveEdit = async (msgId) => {
    setIsSavingEdit(true);
    try {
      await ticketsAPI.editMessage(msgId, { new_message: editValue });

      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId ? { ...m, message: editValue, is_edited: true } : m,
        ),
      );
      setEditingMessageId(null);
    } catch (error) {
      toast({ title: "Erro ao editar", variant: "destructive" });
    } finally {
      setIsSavingEdit(false);
    }
  };

  useEffect(() => {
    if (!editingMessageId) return;

    if (editInputRef.current) {
      const input = editInputRef.current;
      input.focus();
      const length = input.value.length;
      input.setSelectionRange(length, length);
    }

    isUserScrolledUp.current = false;
    setIsScrolledUp(false);
    setUnreadInChat(0);

    scrollToBottom(true);
    const t = setTimeout(() => scrollToBottom(false), 50);

    return () => clearTimeout(t);
  }, [editingMessageId]);

  const handleDeleteMsg = async (msgId) => {
    if (deletingId) return;

    const { dismiss } = toast({
      title: "Apagar mensagem?",
      description: "Esta ação apagará a mensagem para todos no WhatsApp.",
      variant: "default",
      duration: 10000,
      action: (
        <div className="flex gap-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={async (e) => {
              const btn = e.currentTarget;
              btn.disabled = true;
              btn.innerHTML =
                '<div class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div> Apagando...';

              setDeletingId(msgId);

              try {
                await ticketsAPI.deleteMessage(msgId);

                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === msgId
                      ? {
                          ...m,
                          message: "🚫 Mensagem apagada",
                          is_deleted: true,
                        }
                      : m,
                  ),
                );

                dismiss();
              } catch (err) {
                btn.disabled = false;
                btn.innerHTML = "Confirmar";
                toast({
                  title: "Erro ao apagar",
                  description: "Não foi possível apagar no WhatsApp.",
                  variant: "destructive",
                });
              } finally {
                setDeletingId(null);
              }
            }}
          >
            Confirmar
          </Button>

          <Button variant="outline" size="sm" onClick={() => dismiss()}>
            Cancelar
          </Button>
        </div>
      ),
    });
  };

  const startTimer = () => {
    clearInterval(timerRef.current);
    recordingStartedAtRef.current = performance.now();
    timerRef.current = setInterval(() => {
      setRecordingTime((recordingElapsedRef.current + performance.now() - recordingStartedAtRef.current) / 1000);
    }, 200);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recordingStartedAtRef.current !== null) {
      recordingElapsedRef.current += performance.now() - recordingStartedAtRef.current;
      recordingStartedAtRef.current = null;
      setRecordingTime(recordingElapsedRef.current / 1000);
    }
  };

  const clearAudioPreview = () => {
    audioPreviewRef.current?.pause();
    if (audioPreviewUrlRef.current) {
      URL.revokeObjectURL(audioPreviewUrlRef.current);
      audioPreviewUrlRef.current = null;
    }
    setRecordedAudio(null);
  };

  const handleStartRecording = async () => {
    if (
      !canReply || recordedAudio || mediaRecorderRef.current ||
      recordingRequestRef.current || audioSendInFlightRef.current
    ) return;

    const request = {};
    recordingRequestRef.current = request;
    setIsStartingRecording(true);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (recordingRequestRef.current !== request) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      const preparePreview = () => {
        const contentType = recorder.mimeType || "audio/webm";
        const audioBlob = new Blob(audioChunksRef.current, { type: contentType });
        if (!audioBlob.size) return null;
        const extension = contentType.includes("mp4") ? "m4a" : contentType.includes("ogg") ? "ogg" : "webm";
        const file = new File([audioBlob], `audio_${Date.now()}.${extension}`, { type: contentType });
        clearAudioPreview();
        const audio = { file, url: URL.createObjectURL(audioBlob), ticketId };
        audioPreviewUrlRef.current = audio.url;
        setRecordedAudio(audio);
        return audio;
      };

      recorder.ondataavailable = (e) => {
        if (mediaRecorderRef.current !== recorder) return;
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
        if (recorder.state === "paused" && !finishRecordingRef.current) {
          preparePreview();
          setIsPreparingAudio(false);
        }
      };

      recorder.onstop = () => {
        if (mediaRecorderRef.current !== recorder) return;
        mediaRecorderRef.current = null;
        recorder.stream.getTracks().forEach((track) => track.stop());
        stopTimer();
        setIsRecording(false);
        setIsPaused(false);
        setIsPreparingAudio(false);
        const audio = preparePreview();
        audioChunksRef.current = [];
        const finish = finishRecordingRef.current;
        finishRecordingRef.current = null;
        finish?.(audio);

        if (!audio) {
          toast({ title: "Nenhum áudio gravado", description: "Grave novamente antes de enviar.", variant: "destructive" });
        }
      };

      recorder.start();
      setIsRecording(true);
      setIsPaused(false);
      recordingElapsedRef.current = 0;
      setRecordingTime(0);
      startTimer();
    } catch (err) {
      stream?.getTracks().forEach((track) => track.stop());
      if (recordingRequestRef.current !== request) return;
      mediaRecorderRef.current = null;
      toast({
        title: "Microfone bloqueado",
        description:
          "Verifique se o navegador tem permissão para acessar o microfone.",
        variant: "destructive",
      });
    } finally {
      if (recordingRequestRef.current === request) {
        recordingRequestRef.current = null;
        setIsStartingRecording(false);
      }
    }
  };

  const handleSendRecordedAudio = async () => {
    if ((!recordedAudio && !isRecording) || audioSendInFlightRef.current || !canReply) return;
    if (recordedAudio && recordedAudio.ticketId !== ticketId) return;

    audioSendInFlightRef.current = true;
    setSendingAudio(true);
    audioPreviewRef.current?.pause();

    try {
      let audio = recordedAudio;
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        const finished = new Promise((resolve) => { finishRecordingRef.current = resolve; });
        setIsPreparingAudio(true);
        stopTimer();
        recorder.stop();
        recorder.stream.getTracks().forEach((track) => track.stop());
        audio = await finished;
      }
      if (!audio || activeTicketIdRef.current !== audio.ticketId) return;
      const quotedId = replyingTo ? replyingTo.wa_message_id || replyingTo.id : null;
      const quotedText = replyingTo ? replyingTo.message : null;
      const quotedFromMe = replyingTo ? !replyingTo.is_from_client : false;
      await ticketsAPI.uploadFile(audio.ticketId, audio.file, quotedId, quotedText, quotedFromMe);

      if (activeTicketIdRef.current === audio.ticketId) {
        clearAudioPreview();
        setRecordingTime(0);
        setReplyingTo(null);
        loadMessages();
        toast({ title: "Áudio enviado" });
      }
    } catch (error) {
      if (activeTicketIdRef.current === ticketId) {
        toast({ title: "Erro ao enviar áudio", description: "O áudio foi mantido para você tentar novamente.", variant: "destructive" });
      }
    } finally {
      finishRecordingRef.current = null;
      setIsPreparingAudio(false);
      audioSendInFlightRef.current = false;
      setSendingAudio(false);
    }
  };

  const handlePauseRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      stopTimer();
      setIsPreparingAudio(true);
      mediaRecorderRef.current.requestData();
    }
  };

  const handleResumeRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      clearAudioPreview();
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimer();
    }
  };

  const handleCancelRecording = () => {
    recordingRequestRef.current = null;
    audioChunksRef.current = [];
    const recorder = mediaRecorderRef.current;
    if (recorder) {
      recorder.onstop = null;
      recorder.ondataavailable = null;
      if (recorder.state !== "inactive") recorder.stop();
      recorder.stream.getTracks().forEach((track) => track.stop());
      mediaRecorderRef.current = null;
    }
    stopTimer();
    clearAudioPreview();
    setIsStartingRecording(false);
    setIsPreparingAudio(false);
    setRecordingTime(0);
    setIsRecording(false);
    setIsPaused(false);
  };

  const handleAudioEnded = (currentIndex) => {
    const currentMsg = messages[currentIndex];
    const nextMsg = messages[currentIndex + 1];

    if (!nextMsg) return;

    let isNextAudio = false;

    if (
      nextMsg.message_type === "audio" ||
      nextMsg.message?.includes(".ogg") ||
      nextMsg.audio_url
    ) {
      isNextAudio = true;
    } else if (nextMsg.attachment) {
      const att = nextMsg.attachment;
      const contentType = att.content_type || att.mime_type || "";
      const filename = att.filename || att.name || "";

      if (
        contentType.includes("audio") ||
        filename.match(/\.(webm|ogg|mp3|wav|m4a|aac|opus)$/i)
      ) {
        isNextAudio = true;
      }
    }

    if (isNextAudio) {
      const nextAudioElement = audioRefs.current[nextMsg.id];

      if (nextAudioElement) {
        nextAudioElement.play().catch((error) => {
          console.error("Navegador bloqueou o auto-play do áudio:", error);
        });
      }
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await welcomeQuestionsAPI.getAll();
      setDepartments(response.data);
    } catch (error) {
      console.error("Erro ao carregar departamentos:", error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    }
  };

  const executeWithDepartmentCheck = (actionCallback) => {
    if (!ticket?.selected_menu_option || ticket.selected_menu_option === "") {
      setPendingAction(() => actionCallback);
      setRequiredDepartment("");
      setShowDepartmentPrompt(true);
    } else {
      return actionCallback();
    }
  };

  const confirmDepartmentAndProceed = async () => {
    if (!requiredDepartment) {
      toast({
        title: "Erro",
        description: "Selecione um departamento.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await ticketsAPI.update(ticket.id, {
        selected_menu_option: requiredDepartment,
        menu_response_received: true,
      });

      setShowDepartmentPrompt(false);
      setTicket(response.data);

      if (pendingAction) {
        await pendingAction();
      }

    } catch (error) {
      toast({
        title: "Erro",
        description: getTicketErrorMessage(error, "definir o departamento"),
        variant: "destructive",
      });
    }
  };

  const currentChatId = useRef(ticket?.id);

  const loadQuickReplies = async () => {
    try {
      const response = await autoResponsesAPI.getAll();
      setQuickReplies(response.data);
    } catch (error) {
      console.error("Erro ao carregar respostas rápidas:", error);
    }
  };

  const markTicketAsReadQuietly = () => {
    if (!ticketId) return;

    const now = Date.now();
    if (now - lastMarkAsReadAtRef.current < 5000) return;

    lastMarkAsReadAtRef.current = now;
    ticketsAPI.markAsRead(ticketId).catch(() => {});
  };

  const scrollToBottom = (instant = false, force = false) => {
    if (isUserScrolledUp.current && !force) return;

    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: instant ? "auto" : "smooth",
      });

      setUnreadInChat(0);
      isUserScrolledUp.current = false;
      setIsScrolledUp(false);
      markTicketAsReadQuietly();
    }
  };

  useEffect(() => {
    if (ticket?.id !== currentChatId.current) {
      currentChatId.current = ticket?.id;
      isUserScrolledUp.current = false;
      setIsScrolledUp(false);
      setUnreadInChat(0);
      prevMessagesLengthRef.current = messages.length;
      lastMessageIdRef.current =
        messages.length > 0 ? messages[messages.length - 1].id : null;

      scrollToBottom(true, true);
      const t1 = setTimeout(() => scrollToBottom(true, true), 150);
      return () => clearTimeout(t1);
    }

    if (messages.length === 0) return;

    if (isLoadingOldMessagesRef.current) {
      prevMessagesLengthRef.current = messages.length;
      lastMessageIdRef.current = messages[messages.length - 1].id;
      return;
    }

    const latestMessage = messages[messages.length - 1];

    if (!lastMessageIdRef.current) {
      lastMessageIdRef.current = latestMessage.id;
      setTimeout(() => scrollToBottom(true, true), 50);
      return;
    }

    if (latestMessage.id !== lastMessageIdRef.current) {
      const isSystemMessage = latestMessage?.sender?.includes("Sistema");
      const isOwnMessage = !latestMessage?.is_from_client && !isSystemMessage;

      if (isOwnMessage) {
        setTimeout(() => scrollToBottom(false, true), 50);
      } else {
        if (!isUserScrolledUp.current) {
          setTimeout(() => scrollToBottom(false, true), 50);
        } else {
          setUnreadInChat((prev) => prev + 1);
        }
      }

      lastMessageIdRef.current = latestMessage.id;
    }

    prevMessagesLengthRef.current = messages.length;
  }, [messages, ticket?.id]);

  const checkIsAtBottom = (container, tolerance = 150) => {
    if (!container) return false;
    const { scrollTop, scrollHeight, clientHeight } = container;
    return Math.ceil(scrollTop + clientHeight) >= scrollHeight - tolerance;
  };

  const handleScroll = () => {
    if (
      !chatContainerRef.current ||
      isLoadingMore ||
      isLoadingOldMessagesRef.current ||
      ignoreScrollEventsRef.current
    )
      return;

    const isAtBottom = checkIsAtBottom(chatContainerRef.current, 150);

    isUserScrolledUp.current = !isAtBottom;
    setIsScrolledUp(!isAtBottom);

    if (isAtBottom) {
      setUnreadInChat(0);
      markTicketAsReadQuietly();
    }
  };

  const sendHeartbeat = async () => {
    try {
      await usersAPI.heartbeat();
    } catch (error) {}
  };

  const getAttendanceStartTime = (ticket) =>
    ticket?.attendance_started_at || ticket?.started_at || ticket?.created_at;

  const loadActiveTickets = async () => {
    if (activeTicketsLoadingRef.current) return;
    activeTicketsLoadingRef.current = true;

    try {
      const response = await ticketsAPI.getAll({ limit: 1000 });
      const todosTickets = response.data.items || response.data || [];

      const active = todosTickets.filter(
        (t) => t.status === "in_progress" && t.assigned_to === user?.id,
      );
      active.sort(
        (a, b) =>
          new Date(getAttendanceStartTime(a)) -
          new Date(getAttendanceStartTime(b)),
      );
      setMyActiveTickets(active);

      const pendentes = todosTickets.filter(
        (t) => t.status === "pending" && t.menu_response_received === true,
      );
      pendentes.sort(
        (a, b) =>
          new Date(getAttendanceStartTime(a)) -
          new Date(getAttendanceStartTime(b)),
      );
      setPendingTickets(pendentes);

      const potenciais = todosTickets.filter(
        (t) => t.status === "pending" && t.menu_response_received === false,
      );
      potenciais.sort(
        (a, b) =>
          new Date(getAttendanceStartTime(a)) -
          new Date(getAttendanceStartTime(b)),
      );
      setPotentialTickets(potenciais);
    } catch (error) {
      console.error("Erro ao carregar atendimentos da barra lateral", error);
    } finally {
      activeTicketsLoadingRef.current = false;
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadActiveTickets();
      const interval = setInterval(loadActiveTickets, 5000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const formatTicketTime = (isoString) => {
    if (!isoString) return "";

    const date = new Date(isoString);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    if (isToday) {
      return `Hoje às ${date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    }

    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const loadTicket = async () => {
    if (!ticketId) {
      setLoading(false);
      return;
    }

    try {
      const response = await ticketsAPI.getById(ticketId);
      if (resolvingRef.current || completedClosureRef.current === ticketId || activeTicketIdRef.current !== ticketId) return;
      setTicket(response.data);
    } catch (error) {
      if (resolvingRef.current || completedClosureRef.current === ticketId || activeTicketIdRef.current !== ticketId) return;
      const ticketWasTakenByAnotherAttendant =
        error.response?.status === 403 &&
        error.response?.data?.detail ===
          "Você não tem permissão para visualizar este atendimento.";

      toast({
        title: ticketWasTakenByAnotherAttendant
          ? "Atendimento assumido!"
          : "Erro",
        description: ticketWasTakenByAnotherAttendant
          ? error.response.data.assigned_to_name
            ? `O atendente ${error.response.data.assigned_to_name} assumiu este atendimento.`
            : "Outro atendente assumiu este atendimento."
          : getTicketErrorMessage(error, "carregar o atendimento"),
        variant: "destructive",
      });
      navigate("/chat");
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (isLoadMore = false) => {
    if (!isLoadMore && messagesLoadingRef.current) return;

    try {
      if (isLoadMore) setIsLoadingMore(true);
      else messagesLoadingRef.current = true;

      const response = await ticketsAPI.getMessages(ticketId, {
        limit: messageLimitRef.current,
        scope: messageScope,
      });

      const newMessages = response.data.messages || response.data;

      setMessages((prevMessages) => {
        if (isLoadMore) return newMessages;

        const isDifferent =
          prevMessages.length !== newMessages.length ||
          newMessages.some(
            (msg, index) =>
              getMessageRevision(msg) !== getMessageRevision(prevMessages[index] || {}),
          );

        return isDifferent ? newMessages : prevMessages;
      });

      if (response.data.has_more !== undefined) {
        setHasMoreMessages(response.data.has_more);
      }

      if (!isLoadMore && chatContainerRef.current) {
        const isAtBottom = checkIsAtBottom(chatContainerRef.current, 150);
        if (isAtBottom) {
          markTicketAsReadQuietly();
        }
      }
    } catch (error) {
      console.error("Error loading messages:", error);
    } finally {
      if (isLoadMore) setIsLoadingMore(false);
      else messagesLoadingRef.current = false;
    }
  };

  const handleLoadMoreMessages = async () => {
    const container = chatContainerRef.current;
    if (!container) return;

    isLoadingOldMessagesRef.current = true;
    ignoreScrollEventsRef.current = true;

    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;

    messageLimitRef.current += 50;

    await loadMessages(true);

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (chatContainerRef.current) {
          const newScrollHeight = chatContainerRef.current.scrollHeight;
          chatContainerRef.current.scrollTop =
            previousScrollTop + (newScrollHeight - previousScrollHeight);
        }

        setTimeout(() => {
          isLoadingOldMessagesRef.current = false;

          setTimeout(() => {
            ignoreScrollEventsRef.current = false;
          }, 500);
        }, 50);
      }, 100);
    });
  };

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll({ active_only: true });
      setUsers(response.data.filter((u) => u.id !== user?.id));
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    }
  };

  const handleSendMessage = async () => {
    const message =
      showQuickMenu && filteredReplies.length === 1
        ? getQuickReplyMessage(filteredReplies[0].response)
        : newMessage;

    if (!message.trim() && !fileInputRef.current?.files?.length) return;

    try {
      setSendingMessage(true);

      const payload = {
        message,
        quoted_message_id: replyingTo ? replyingTo.wa_message_id : null,
        quoted_message_text: replyingTo ? replyingTo.message : null,
        quoted_from_me: replyingTo ? !replyingTo.is_from_client : false,
      };

      await ticketsAPI.sendWhatsApp(ticketId, payload);

      setNewMessage("");
      setShowQuickMenu(false);
      
      if (ticket?.id) sessionStorage.removeItem(`draft_${ticket.id}`);

      setReplyingTo(null);
      scrollToBottom(false, true);

      if (loadMessages) loadMessages();
    } catch (error) {
      toast({ title: "Erro ao enviar", variant: "destructive" });
      console.log(error);
    } finally {
      setSendingMessage(false);

      setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.focus();
        }
      }, 50);
    }
  };

  const handleMessageChange = (e) => {
    const text = e.target.value;

    if (text.endsWith(" ")) {
      const words = text.split(" ");
      const possibleShortcut = words[words.length - 2];
      if (possibleShortcut && possibleShortcut.startsWith("/")) {
        const exactMatch = quickReplies.find(
          (qr) => qr.trigger.toLowerCase() === possibleShortcut.toLowerCase(),
        );
        if (exactMatch) {
          const finalShortcutText = words.join(" ");
          words[words.length - 2] = exactMatch.response;
          const newText = words.join(" ");
          
          setNewMessage(newText);
          setShowQuickMenu(false);
          
          if (ticket?.id) sessionStorage.setItem(`draft_${ticket.id}`, newText);
          return;
        }
      }
    }

    setNewMessage(text);
    
    if (ticket?.id) sessionStorage.setItem(`draft_${ticket.id}`, text);

    const words = text.split(" ");
    const lastWord = words[words.length - 1];

    if (lastWord.startsWith("/")) {
      const matches = quickReplies.filter((qr) =>
        qr.trigger.toLowerCase().startsWith(lastWord.toLowerCase()),
      );
      setFilteredReplies(matches);
      setShowQuickMenu(matches.length > 0);
    } else {
      setShowQuickMenu(false);
    }
  };

  useEffect(() => {
    if (messageInputRef.current) {
      messageInputRef.current.style.height = "auto";

      const scrollHeight = messageInputRef.current.scrollHeight;
      messageInputRef.current.style.height = `${Math.min(scrollHeight, 120)}px`;
    }
  }, [newMessage]);

  useEffect(() => {
    if (ticket?.id) {
      const savedDraft = sessionStorage.getItem(`draft_${ticket.id}`);
      if (savedDraft) {
        setNewMessage(savedDraft);
      } else {
        setNewMessage("");
      }
    }
  }, [ticket?.id]);

  const getQuickReplyMessage = (response) => {
    const words = newMessage.split(" ");
    words.pop();
    return words.length > 0 ? words.join(" ") + " " + response : response;
  };

  const applyQuickReply = (response) => {
    setNewMessage(getQuickReplyMessage(response) + " ");
    setShowQuickMenu(false);
    setTimeout(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }, 50);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSendingFile(true);

      const quotedId = replyingTo
        ? replyingTo.wa_message_id || replyingTo.id
        : null;
      const quotedText = replyingTo ? replyingTo.message : null;
      const quotedFromMe = replyingTo ? !replyingTo.is_from_client : false;

      await ticketsAPI.uploadFile(
        ticketId,
        file,
        quotedId,
        quotedText,
        quotedFromMe,
      );

      setReplyingTo(null);
      loadMessages();
      toast({
        title: "Arquivo enviado",
        description: `${file.name} enviado com sucesso.`,
      });
    } catch (error) {
      const backendError = error.response?.data?.detail;
      const networkError = error.message;

      let errorMessage = "Ocorreu um erro inesperado ao enviar o arquivo.";

      if (backendError) {
        errorMessage =
          typeof backendError === "string"
            ? backendError
            : "Formato de arquivo inválido ou rejeitado pelo servidor.";
      } else if (networkError === "Network Error") {
        errorMessage =
          "Falha de conexão. Verifique sua internet ou se o arquivo é muito pesado para a rede.";
      } else if (error.response?.status === 413) {
        errorMessage =
          "O arquivo é muito grande e foi bloqueado pelo servidor (Nginx/Proxy).";
      }

      toast({
        title: "Erro no envio",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSendingFile(false);
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleTransfer = async () => {
    if (!canManageTicket) {
      setShowTransferDialog(false);
      toast({
        title: "Atendimento finalizado",
        description: "Atendimentos resolvidos ou fechados nao podem ser transferidos.",
        variant: "destructive",
      });
      return;
    }

    const executeTransfer = async () => {
      if (!selectedUser) {
        toast({
          title: "Erro",
          description: "Selecione um atendente.",
          variant: "destructive",
        });
        return;
      }

      try {
        await ticketsAPI.transfer(ticketId, selectedUser, transferReason);
        toast({
          title: "Ticket transferido",
          description: "O ticket foi transferido com sucesso.",
        });
        setShowTransferDialog(false);
        navigate("/chat");
      } catch (error) {
        toast({
          title: "Erro",
          description:
            getTicketErrorMessage(error, "transferir o atendimento"),
          variant: "destructive",
        });
      }
    };

    executeWithDepartmentCheck(executeTransfer);
  };

  const handleUpdateStatus = async (status) => {
    if (!canManageTicket) {
      toast({
        title: "Atendimento finalizado",
        description: "Atendimentos resolvidos ou fechados nao podem ter o status alterado.",
        variant: "destructive",
      });
      return;
    }

    const executeStatusUpdate = async () => {
      if (status === "resolved" || status === "closed") {
        if (isPotentialTicket) {
          toast({ title: "Contato em Potenciais", description: "Use o botão Encerrar atendimento para encerrar sem assumir." });
          return;
        }
        setResolveData({
          category: ticket.category || "",
          description: "",
          status: status,
        });
        setShowResolveDialog(true);
        return;
      }

      try {
        const payload = { status };

        if (status === "in_progress") {
          payload.assigned_to = user.id;
          payload.assigned_to_name = user.name;
        } else if (status === "pending") {
          payload.assigned_to = null;
          payload.assigned_to_name = null;
        }

        await ticketsAPI.update(ticketId, payload);

        toast({
          title: "Status atualizado",
          description:
            status === "in_progress"
              ? "Você assumiu este atendimento."
              : status === "pending"
                ? "Atendimento devolvido para a Fila."
                : "Status alterado com sucesso.",
        });

        if (status === "pending") {
          navigate("/chat");
          return;
        }

        await loadTicket();
        await loadActiveTickets();

        if (status === "in_progress") {
          setActiveSidebarTab("meus");
        }
      } catch (error) {
        toast({
          title: "Erro",
          description: getTicketErrorMessage(error, "alterar o status"),
          variant: "destructive",
        });
      }
    };

    executeWithDepartmentCheck(executeStatusUpdate);
  };

  const handleResolveTicket = async () => {
    if (resolvingRef.current) return;
    if (!canManageTicket) {
      setShowResolveDialog(false);
      toast({
        title: "Atendimento finalizado",
        description: "Atendimentos resolvidos ou fechados nao podem ter o status alterado.",
        variant: "destructive",
      });
      return;
    }

    if (!resolveData.category) {
      toast({
        title: "Erro",
        description: "Selecione uma categoria.",
        variant: "destructive",
      });
      return;
    }
    if (!resolveData.description.trim()) {
      toast({
        title: "Erro",
        description: "Informe a descrição da solução.",
        variant: "destructive",
      });
      return;
    }

    executeWithDepartmentCheck(async () => {
      if (resolvingRef.current) return;
      resolvingRef.current = true;
      setIsResolving(true);
      try {
        const response = await ticketsAPI.update(ticketId, {
          status: resolveData.status,
          resolution_category: resolveData.category,
          resolution_description: resolveData.description,
        });

        toast(getClosureToast(response.data));

        completedClosureRef.current = ticketId;
        setShowResolveDialog(false);
        navigate("/chat");
      } catch (error) {
        toast({
          title: "Erro",
          description: getTicketErrorMessage(error, "finalizar o atendimento"),
          variant: "destructive",
          duration: 10000,
        });
      } finally {
        resolvingRef.current = false;
        setIsResolving(false);
      }
    });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    if (isToday)
      return date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    in_progress: "bg-blue-100 text-blue-800",
    resolved: "bg-green-100 text-green-800",
    closed: "bg-gray-100 text-gray-800",
  };

  const statusLabels = {
    pending: "Pendente",
    in_progress: "Em Andamento",
    resolved: "Resolvido",
    closed: "Fechado",
  };

  const memoizedMessages = React.useMemo(() => {
    return messages.map((msg, index) => {
      const isSystemMessage = msg.sender.includes("Sistema");
      const isTeam = !msg.is_from_client;
      const isEditing = editingMessageId === msg.id;
      const previousMsg = index > 0 ? messages[index - 1] : null;
      const showDivider =
        msg.ticket_id === ticket?.id &&
        previousMsg &&
        previousMsg.ticket_id !== ticket?.id;

      return (
        <React.Fragment key={msg.id || index}>
          {showDivider && (
            <div className="flex items-center justify-center my-8 w-full">
              <div className="bg-gray-300 h-[1px] flex-1"></div>
              <span className="px-4 text-xs uppercase font-black text-gray-500 tracking-wider bg-[#efeae2] border border-gray-300 rounded-full py-1">
                ⬇️ Atendimento Atual ⬇️
              </span>
              <div className="bg-gray-300 h-[1px] flex-1"></div>
            </div>
          )}

          {isSystemMessage ? (
            <div className="flex justify-center my-1.5 w-full">
              <div className="bg-gray-200/80 text-gray-600 px-4 py-2 rounded-lg shadow-sm text-center backdrop-blur-sm border border-gray-300 max-w-2xl">
                <div className="flex items-center justify-center gap-2 mb-1 border-b border-gray-300/50 pb-1">
                  <span className="text-xs sm:text-sm font-bold flex items-center gap-1">
                    🤖 {msg.sender}
                  </span>
                  <span className="text-xs opacity-70 font-medium">
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-xs sm:text-sm leading-relaxed">
                  {msg.message}
                </p>
              </div>
            </div>
          ) : (
            <div
              id={`msg-row-${msg.id}`}
              className={`flex group ${msg.is_from_client ? "justify-start" : "justify-end"} mb-1 w-full transition-colors duration-500 p-1 rounded-lg`}
            >
              <div
                className={`relative flex items-end gap-2 lg:max-w-[85%] ${isTeam ? "flex-row-reverse" : "flex-row"}`}
              >
                <div
                  className={`px-3 py-2 rounded-2xl shadow-sm transition-all flex flex-col min-w-[80px] max-w-full overflow-hidden ${
                    msg.is_from_client
                      ? "bg-white text-gray-900 rounded-tl-none border border-gray-100"
                      : "bg-[#d9fdd3] text-gray-900 rounded-tr-none"
                  } ${msg.is_deleted ? "opacity-60 italic" : ""}`}
                >
                  {msg.quoted_message_id &&
                    (() => {
                      const quotedMsg = messages.find(
                        (m) =>
                          m.wa_message_id === msg.quoted_message_id ||
                          m.id === msg.quoted_message_id,
                      );

                      if (!quotedMsg) return null;

                      const isMainBubbleFromClient = msg.is_from_client;
                      const senderColor = quotedMsg.is_from_client
                        ? "text-purple-600"
                        : "text-emerald-600";

                      return (
                        <div
                          onClick={() => {
                            const el = document.getElementById(
                              `msg-row-${quotedMsg.id}`,
                            );
                            if (el) {
                              el.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                              el.classList.add("bg-blue-500/20");
                              setTimeout(() => {
                                el.classList.remove("bg-blue-500/20");
                              }, 1500);
                            }
                          }}
                          className={`mb-2 p-2 rounded-lg cursor-pointer border-l-[4px] text-sm opacity-95 hover:opacity-100 transition-all ${
                            isMainBubbleFromClient
                              ? "bg-gray-100 border-purple-500 hover:bg-gray-200"
                              : "bg-[#cbf4c9] border-emerald-500 hover:bg-[#bbf0b9]"
                          }`}
                        >
                          <span
                            className={`font-bold block mb-0.5 ${senderColor}`}
                          >
                            {quotedMsg.is_from_client
                              ? ticket?.client_name
                              : quotedMsg.sender}
                          </span>
                          <span className="block line-clamp-2 text-gray-700 leading-snug break-words whitespace-pre-wrap">
                            {quotedMsg.message ||
                              (quotedMsg.attachment
                                ? "📎 Arquivo/Mídia"
                                : "Mensagem")}
                          </span>
                        </div>
                      );
                    })()}

                  <div className="flex flex-row justify-between items-center gap-3 mb-0.5">
                    <span className="text-sm font-bold tracking-wider text-gray-600">
                      {msg.sender}
                    </span>
                    
                    <div className="flex items-center justify-end gap-1 opacity-80">
                      {msg.is_edited && !msg.is_deleted && (
                        <span className="text-xs italic flex items-center gap-0.5 mr-1 text-gray-800">
                          <Pencil className="w-2 h-2" /> editada
                        </span>
                      )}
                      <span className="text-xs text-gray-800">
                        {formatTime(msg.timestamp)}
                      </span>
                      {isTeam && !msg.is_deleted && (
                        <div className="ml-0.5">{renderStatus(msg)}</div>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="flex flex-col gap-2 w-full min-w-[200px] sm:min-w-[350px]">
                      <textarea
                        ref={editInputRef}
                        disabled={isSavingEdit}
                        className="w-full bg-white/50 border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none min-h-[80px]"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            !e.shiftKey &&
                            !isSavingEdit
                          ) {
                            e.preventDefault();
                            handleSaveEdit(msg.id);
                          }
                        }}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditingMessageId(null)}
                          className="p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleSaveEdit(msg.id)}
                          className="p-1.5 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                          {isSavingEdit ? (
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      {msg.message &&
                        !msg.is_deleted &&
                        msg.message.trim().toLowerCase() !==
                          "arquivo enviado" &&
                        !msg.message.startsWith("[Anexo:") && (
                          <p className="whitespace-pre-wrap break-words text-sm sm:text-base text-gray-800">
                            {msg.message}
                          </p>
                        )}
                      {msg.is_deleted && (
                        <p className="text-sm sm:text-base text-gray-500">
                          🚫 Esta mensagem foi apagada
                        </p>
                      )}
                    </div>
                  )}

                  {msg.reaction && !msg.is_deleted && (
                    <div
                      className={`absolute -bottom-3 ${msg.is_from_client ? "-right-2" : "-left-2"} bg-white rounded-full shadow-sm border border-gray-200 px-1.5 py-0.5 text-xs z-10`}
                    >
                      {msg.reaction}
                    </div>
                  )}

                  {!msg.is_deleted && msg.attachment && (
                    <div className={`mt-1 mb-1`}>
                      {(() => {
                        const att = msg.attachment;
                        const url = att.url || att.file_url || att.path || "";
                        const contentType =
                          att.content_type || att.mime_type || "";
                        const filename = att.filename || att.name || "arquivo";

                        if (!url)
                          return (
                            <p className="text-sm italic text-gray-500">
                              📎 Anexo: {filename}
                            </p>
                          );

                        const fullUrl = url.startsWith("http")
                          ? url
                          : `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;

                        if (
                          contentType.includes("image") ||
                          filename.match(/\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i)
                        ) {
                          const isSticker = filename.endsWith(".webp");
                          return (
                            <div
                              onClick={() => {
                                setZoomedImage(fullUrl);
                                setZoomScale(1);
                              }}
                              className="mt-1 block cursor-zoom-in group/img relative"
                            >
                              <img
                                src={fullUrl}
                                alt="Anexo"
                                onLoad={() => scrollToBottom(true, true)}
                                className={`rounded-lg object-contain transition-all bg-black/5 group-hover/img:brightness-90 ${
                                  isSticker
                                    ? "max-h-32 max-w-[128px]"
                                    : "max-w-full max-h-[300px]"
                                }`}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.parentElement.innerHTML = `<p class="text-sm text-gray-600">🖼️ Imagem: ${filename}</p>`;
                                }}
                              />
                            </div>
                          );
                        }

                        if (
                          contentType.includes("audio") ||
                          filename.match(/\.(webm|ogg|mp3|wav|m4a|aac|opus)$/i)
                        ) {
                          return (
                            <audio
                              controls
                              src={fullUrl}
                              ref={(el) => (audioRefs.current[msg.id] = el)}
                              onEnded={() => handleAudioEnded(index)}
                              className="w-full sm:min-w-[250px] h-10 mt-1 outline-none"
                            />
                          );
                        }

                        if (
                          contentType.includes("video") ||
                          filename.match(/\.(mp4|mov|avi|mkv)$/i)
                        ) {
                          return (
                            <video
                              controls
                              src={fullUrl}
                              className="max-w-full rounded-lg max-h-[300px] mt-1 bg-black/5"
                            />
                          );
                        }

                        return (
                          <a
                            href={fullUrl}
                            onClick={(e) =>
                              handleDownload(e, fullUrl, filename)
                            }
                            className="flex items-center gap-2 bg-black/5 mt-1.5 p-2 rounded-lg hover:bg-black/10 text-sm text-gray-700 transition-colors border border-black/5 cursor-pointer"
                          >
                            <div className="bg-white p-1.5 rounded-md shadow-sm shrink-0">
                              <Download className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex flex-col overflow-hidden">
                              <span className="truncate font-medium max-w-[200px]">
                                {filename}
                              </span>
                              <span className="text-[10px] text-gray-500 uppercase">
                                Clique para baixar
                              </span>
                            </div>
                          </a>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {!msg.is_deleted && !isEditing && canReply && (
                  <div className="flex flex-col items-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={() => handleReplyClick(msg)}
                      className="p-1.5 bg-white shadow-sm border border-gray-200 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                      title="Responder"
                    >
                      <Reply className="w-3 h-3" />
                    </button>
                    {isTeam && canModifyTicketConversation && (
                      <div className="flex-row gap-2">
                        <button
                          onClick={() => {
                            setEditingMessageId(msg.id);
                            setEditValue(msg.message);
                          }}
                          className="p-1.5 bg-white shadow-sm border border-gray-200 rounded-full text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                          title="Editar"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteMsg(msg.id)}
                          disabled={deletingId === msg.id}
                          className="p-1.5 bg-white shadow-sm border border-gray-200 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                          title="Apagar para todos"
                        >
                          {deletingId === msg.id ? (
                            <div className="animate-spin w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </React.Fragment>
      );
    });
  }, [
    messages,
    ticket?.id,
    ticket?.client_name,
    editingMessageId,
    editValue,
    isSavingEdit,
    deletingId,
    canReply,
    canModifyTicketConversation,
  ]);

  return (
    <div
      className="h-full w-full flex bg-gray-50 overflow-hidden"
      data-testid="chat-page"
    >
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] md:hidden backdrop-blur-sm"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <div
        className={`
          fixed inset-y-0 left-0 z-[70] bg-white border-r transition-all duration-300 ease-in-out flex flex-col shrink-0
          md:relative md:translate-x-0 md:z-auto
          ${isMobileSidebarOpen ? "max-w-[85%] translate-x-0 w-80" : "-translate-x-full md:translate-x-0"}
          ${isSidebarCollapsed ? "md:w-0 md:opacity-0 md:border-transparent md:overflow-hidden" : "md:w-80 md:opacity-100"}
        `}
      >
        <div className="flex bg-gradient-to-r from-blue-600 to-blue-700 text-white shrink-0 shadow-sm relative">
          <button
            onClick={() => setActiveSidebarTab("meus")}
            className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-3 px-2 border-b-2 transition-colors ${
              activeSidebarTab === "meus"
                ? "border-white bg-white/10"
                : "border-transparent hover:bg-white/5 opacity-80"
            }`}
          >
            <span className="font-semibold tracking-wide text-xs">Ativos</span>
            <Badge className="bg-white/20 text-white hover:bg-white/30 border-none px-1.5 py-0 text-xs">
              {myActiveTickets.length}
            </Badge>
          </button>

          <button
            onClick={() => setActiveSidebarTab("pendentes")}
            className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-3 px-2 border-b-2 transition-colors ${
              activeSidebarTab === "pendentes"
                ? "border-white bg-white/10"
                : "border-transparent hover:bg-white/5 opacity-80"
            }`}
          >
            <span className="font-semibold tracking-wide text-xs">Fila</span>
            <Badge
              className={`border-none px-1.5 py-0 text-xs ${pendingTickets.length > 0 ? "bg-red-500 text-white hover:bg-red-600" : "bg-white/20 text-white hover:bg-white/30"}`}
            >
              {pendingTickets.length}
            </Badge>
          </button>

          <button
            onClick={() => setActiveSidebarTab("potenciais")}
            className={`flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-3 px-2 border-b-2 transition-colors ${
              activeSidebarTab === "potenciais"
                ? "border-white bg-white/10"
                : "border-transparent hover:bg-white/5 opacity-80"
            }`}
          >
            <span className="font-semibold tracking-wide text-xs">
              Potenciais
            </span>
            <Badge
              className={`border-none px-1.5 py-0 text-xs ${potentialTickets.length > 0 ? "bg-orange-500 text-white hover:bg-orange-600" : "bg-white/20 text-white hover:bg-white/30"}`}
            >
              {potentialTickets.length}
            </Badge>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50/50">
          {activeSidebarTab === "meus" && myActiveTickets.length === 0 && (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400">
              <Inbox className="w-8 h-8 mb-2 opacity-50 shrink-0" />
              <p className="text-sm text-center">Nenhum atendimento ativo</p>
            </div>
          )}

          {activeSidebarTab === "pendentes" && pendingTickets.length === 0 && (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400">
              <Inbox className="w-8 h-8 mb-2 opacity-50 shrink-0" />
              <p className="text-sm text-center">Nenhum atendimento pendente</p>
            </div>
          )}

          {activeSidebarTab === "potenciais" &&
            potentialTickets.length === 0 && (
              <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                <Inbox className="w-8 h-8 mb-2 opacity-50 shrink-0" />
                <p className="text-sm text-center">
                  Nenhum atendimento em potencial
                </p>
              </div>
            )}

          {(activeSidebarTab === "meus"
            ? myActiveTickets
            : activeSidebarTab === "pendentes"
              ? pendingTickets
              : potentialTickets
          ).map((t) => {
            const isCurrentTicket = t.id === ticketId;
            const backgroundStyle = isCurrentTicket
              ? "bg-orange-100"
              : "bg-white";
            const borderStyle = isCurrentTicket
              ? "border-l-blue-600 bg-blue-50"
              : activeSidebarTab === "pendentes"
                ? "border-l-red-400 hover:bg-gray-50"
                : activeSidebarTab === "potenciais"
                  ? "border-l-orange-400 hover:bg-gray-50"
                  : "border-l-transparent hover:bg-gray-50";

            return (
              <div
                key={t.id}
                onClick={() => {
                  navigate(`/chat/${t.id}`);
                  setIsMobileSidebarOpen(false);
                }}
                className={`p-4 border-b border-l-4 cursor-pointer transition-all duration-200 group relative ${backgroundStyle} ${borderStyle}`}
              >
                <div className="flex justify-between items-start mb-1.5">
                  <h3
                    className={`font-semibold text-sm truncate pr-2 ${isCurrentTicket ? "text-blue-700" : "text-gray-900 group-hover:text-blue-600"}`}
                  >
                    {t.client_name || "Cliente sem nome"}
                  </h3>
                  <span
                    className={`text-xs shrink-0 font-medium ${isCurrentTicket ? "text-blue-500" : "text-gray-400"}`}
                  >
                    {formatTicketTime(getAttendanceStartTime(t))}
                  </span>
                </div>

                <p className="text-xs text-gray-500 truncate pr-4">
                  {t.last_message
                    ? t.last_message
                    : t.status === "pending"
                      ? t.menu_response_received
                        ? "Aguardando atendimento na fila..."
                        : "Navegando no menu..."
                      : "Em andamento..."}
                </p>

                {t.unread_count > 0 && activeSidebarTab === "meus" && (
                  <span className="absolute right-4 bottom-4 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse shadow-sm">
                    {t.unread_count} nova(s)
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex-1 flex flex-col relative h-full min-w-0 z-0">
        {!ticketId ? (
          <div className="flex flex-col items-center justify-center h-full bg-gray-50/50 relative">
            {!isMobileSidebarOpen && (
              <div className="absolute top-4 left-4 md:hidden z-10">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsMobileSidebarOpen(true)}
                >
                  <MessagesSquare className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="font-semibold text-gray-700">Conversas</span>
                </Button>
              </div>
            )}

            {isSidebarCollapsed && (
              <div className="absolute top-4 left-4 hidden md:block z-10">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSidebar}
                  className="flex items-center text-gray-600 shadow-sm"
                >
                  <ChevronRight className="w-5 h-5 mr-2" />
                  <span>Mostrar Atendimentos</span>
                </Button>
              </div>
            )}

            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
              <MessageCircle className="w-10 h-10 text-blue-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
              Central de Atendimentos
            </h2>
            <p className="text-gray-500 text-center max-w-sm px-4">
              Selecione um atendimento na barra lateral ou resgate um cliente da
              fila para iniciar.
            </p>

            <Button
              className="md:hidden mt-6 z-10"
              onClick={() => setIsMobileSidebarOpen(true)}
            >
              <MessagesSquare className="w-4 h-4 mr-2" />
              Abrir Lista de Conversas
            </Button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          </div>
        ) : !ticket ? (
          <div className="flex flex-col items-center justify-center h-full relative">
            {!isMobileSidebarOpen && (
              <div className="absolute top-4 left-4 md:hidden z-10">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsMobileSidebarOpen(true)}
                >
                  <MessagesSquare className="w-5 h-5 mr-2 text-blue-600" />
                  Conversas
                </Button>
              </div>
            )}
            <Archive className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">Ticket não encontrado</p>
            <Button
              onClick={() => navigate("/chat")}
              variant="outline"
              className="mt-4"
            >
              Voltar aos atendimentos
            </Button>
          </div>
        ) : (
          <>
            <div className="relative bg-white border-b py-3 sm:py-4 flex flex-col items-stretch z-0">
              <div
                className={`flex items-center gap-2 mb-3 md:mb-0 md:absolute md:left-4 z-10 ${isMobileSidebarOpen ? "hidden md:flex" : ""}`}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/")}
                  className="md:hidden flex items-center justify-center h-9 w-9 p-0 shrink-0 bg-gray-50 border border-gray-200 rounded-lg"
                  title="Voltar ao início"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSidebar}
                  className="flex items-center text-gray-500 hover:bg-gray-100 h-9 md:w-9 px-3 md:p-0 shrink-0 border border-gray-200 md:border-transparent rounded-lg md:rounded-md bg-gray-50 md:bg-transparent"
                >
                  <div className="md:hidden flex items-center text-blue-600 font-semibold">
                    <MessagesSquare className="w-5 h-5 mr-1.5" />
                    <span>Conversas</span>
                  </div>

                  <div className="hidden md:flex items-center justify-center w-full h-full">
                    {isSidebarCollapsed ? (
                      <ChevronRight className="w-5 h-5" />
                    ) : (
                      <ChevronLeft className="w-5 h-5" />
                    )}
                  </div>
                </Button>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={toggleHeader}
                className="absolute top-4 sm:top-5 right-2 text-gray-500 hover:bg-gray-100 z-10"
                title={headerExpanded ? "Ocultar detalhes" : "Mostrar detalhes"}
              >
                {headerExpanded ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </Button>

              <div className="flex flex-col 2xl:flex-row justify-between gap-3 sm:pr-[50px] sm:pl-[65px]">
                <div className="flex items-start gap-3 w-full 2xl:w-auto min-w-0">
                  <div className="flex flex-col min-w-0 w-full">
                    <div className="flex flex-row gap-2 items-center">
                      {ticket.client_pfp_url ? (
                        <img
                          src={ticket.client_pfp_url}
                          alt={ticket.client_name}
                          onClick={() => setIsProfilePicOpen(true)}
                          className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm cursor-pointer hover:opacity-80 transition-opacity"
                          title="Ver foto"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "https://api.dicebear.com/8.x/initials/svg?seed=Generico";
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-between text-gray-500 font-semibold text-lg uppercase tracking-tight shadow-inner">
                          <span className="w-full text-center">
                            {(ticket.client_name || "C").substring(0, 1)}
                          </span>
                        </div>
                      )}
                      <h1 className="text-lg sm:text-xl font-bold truncate max-w-[200px] sm:max-w-md">
                        {ticket.client_name}
                      </h1>
                      {canEditContact && (
                        <button
                          onClick={handleOpenEditClient}
                          className="text-gray-400 hover:text-blue-600 transition-colors mt-1"
                          title="Editar dados do contato"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div
                      className={`transition-all duration-300 ease-in-out origin-top overflow-hidden ${
                        headerExpanded
                          ? "max-h-[500px] opacity-100 mt-3"
                          : "max-h-0 opacity-0 mt-0"
                      }`}
                    >
                      <div className="flex flex-col gap-2 text-sm text-gray-600 bg-gray-50/50 p-3 rounded-lg border border-gray-100">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {ticket.external_client_data?.real_phone ? (
                              <span
                                className="font-medium text-green-700"
                                title="Número Real (Agenda)"
                              >
                                {formatWhatsAppNumber(
                                  ticket.external_client_data.real_phone,
                                )}
                              </span>
                            ) : ticket.client_phone.length > 14 ? (
                              `ID Oculto (${ticket.client_phone})`
                            ) : (
                              formatWhatsAppNumber(ticket.client_phone)
                            )}
                          </span>

                          {ticket.external_client_company && (
                            <span className="flex items-center gap-1 text-purple-700 font-medium bg-purple-100/50 px-2 py-0.5 rounded-md border border-purple-100">
                              <Building2 className="w-4 h-4" />
                              {ticket.external_client_company}
                            </span>
                          )}

                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-gray-400" />
                            Protocolo:{" "}
                            <span className="font-medium">
                              {ticket.protocol}
                            </span>
                          </span>

                          {ticket.created_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              Início:{" "}
                              <span className="font-medium">
                                {new Date(ticket.created_at)
                                  .toLocaleDateString("pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                  .replace(", ", " às ")}
                              </span>
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          {ticket.priority && (
                            <span
                              className={`px-2 py-0.5 rounded-md text-xs font-bold border uppercase tracking-wider shadow-sm
                          ${
                            ticket.priority === "urgent"
                              ? "bg-red-50 text-red-700 border-red-200"
                              : ticket.priority === "high"
                                ? "bg-orange-50 text-orange-700 border-orange-200"
                                : ticket.priority === "medium"
                                  ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                  : "bg-green-50 text-green-700 border-green-200"
                          }`}
                            >
                              {ticket.priority === "urgent"
                                ? "🚨 Urgente"
                                : ticket.priority === "high"
                                  ? "🔥 Alta"
                                  : ticket.priority === "medium"
                                    ? "⚡ Média"
                                    : "🟢 Baixa"}
                            </span>
                          )}

                          {ticket.category &&
                            ticket.category !== "Sem Categoria" && (
                              <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1 shadow-sm">
                                🏷️ {ticket.category}
                              </span>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full 2xl:w-auto shrink-0 border-t 2xl:border-t-0 pt-3 2xl:pt-0">
                  <div className="flex flex-wrap items-center gap-2 justify-start 2xl:justify-end">
                    <div className="flex shrink-0 items-center gap-2">
                      <Dialog
                        open={showProcedureRecordings}
                        onOpenChange={setShowProcedureRecordings}
                      >
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowProcedureRecordings(true)}
                          className="border-violet-200 px-3 sm:px-4 text-violet-700 hover:bg-violet-50"
                        >
                          Procedimentos
                          {procedureRecordingsCount > 0 && (
                            <span className="ml-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-xs font-bold">
                              {procedureRecordingsCount}
                            </span>
                          )}
                        </Button>
                        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Procedimentos deste atendimento</DialogTitle>
                            <DialogDescription>
                              Vídeos vinculados ao protocolo {ticket.protocol}.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="pt-2">
                            <ProcedureRecordingsList
                              ticketId={ticket.id}
                              refreshKey={procedureRecordingsRefreshKey}
                              onCountChange={setProcedureRecordingsCount}
                              compact
                            />
                          </div>
                        </DialogContent>
                      </Dialog>

                      <ProcedureRecorderDialog
                        ticket={ticket}
                        disabled={!canReply}
                        triggerClassName="px-3 md:px-4"
                        onSaved={() => {
                          setProcedureRecordingsRefreshKey((current) => current + 1);
                          setShowProcedureRecordings(true);
                          toast({ title: "Procedimento salvo", description: "O vídeo está pronto para reprodução." });
                        }}
                      />
                    </div>

                    <ProcedureRecordingPickerDialog
                      open={showProcedurePicker}
                      onOpenChange={setShowProcedurePicker}
                      ticket={ticket}
                      onSend={sendSelectedProcedureRecordings}
                    />

                    {ticket?.status === "pending" && (
                      <Button
                        onClick={() => handleUpdateStatus("in_progress")}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Assumir
                      </Button>
                    )}

                    {isPotentialTicket && (
                      <ClosePotentialTicketButton
                        ticket={ticket}
                        className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        onClosingChange={(closing) => { resolvingRef.current = closing; }}
                        onClosed={() => {
                          completedClosureRef.current = ticket.id;
                          navigate("/chat");
                        }}
                      />
                    )}

                    {!canManageTicket && ticket?.status && (
                      <Badge
                        className={`${statusColors[ticket.status] || "bg-gray-100 text-gray-800"} border px-3 py-1.5 text-sm font-semibold`}
                      >
                        {statusLabels[ticket.status] || ticket.status}
                      </Badge>
                    )}

                    {isFinalizedTicket && (
                      <Dialog
                        open={showClosureDetailsDialog}
                        onOpenChange={setShowClosureDetailsDialog}
                      >
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowClosureDetailsDialog(true)}
                          className="border-slate-300 text-slate-700 hover:bg-slate-50"
                          data-testid="closure-details-button"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Detalhes do encerramento
                        </Button>
                        <DialogContent className="w-full max-w-[95vw] sm:max-w-lg max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-xl sm:text-2xl">
                              <CheckCircle className="w-6 h-6 text-green-600 shrink-0" />
                              Detalhes do encerramento
                            </DialogTitle>
                            <DialogDescription>
                              Informações registradas ao finalizar o protocolo {ticket.protocol}.
                            </DialogDescription>
                          </DialogHeader>

                          <div className="space-y-4 pt-2">
                            <div className="rounded-lg border bg-slate-50 p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Status final
                              </p>
                              <div className="mt-2 flex items-center gap-2">
                                <Badge
                                  className={`${statusColors[ticket.status] || "bg-gray-100 text-gray-800"} border px-3 py-1 text-sm font-semibold`}
                                >
                                  {statusLabels[ticket.status] || ticket.status}
                                </Badge>
                                {ticket.finished_at && (
                                  <span className="text-sm text-slate-500">
                                    em {formatTime(ticket.finished_at)}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <p className="text-sm font-bold text-gray-700">
                                Categoria do encerramento
                              </p>
                              <p className="rounded-lg border bg-white px-3 py-2.5 text-sm text-gray-800 whitespace-pre-wrap">
                                {ticket.resolution_category || ticket.category || "Não informada"}
                              </p>
                            </div>

                            <div className="space-y-1.5">
                              <p className="text-sm font-bold text-gray-700">
                                Descrição do encerramento
                              </p>
                              <p className="min-h-24 rounded-lg border bg-white px-3 py-2.5 text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">
                                {ticket.resolution_description || "Nenhuma descrição foi registrada para este encerramento."}
                              </p>
                            </div>

                            <div className="flex justify-end pt-2">
                              <Button
                                variant="outline"
                                onClick={() => setShowClosureDetailsDialog(false)}
                              >
                                Fechar
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}

                    {!isSpectator && canManageTicket && (
                      <>
                        <Select
                          value={ticket.status}
                          onValueChange={handleUpdateStatus}
                        >
                          <SelectTrigger className="w-[140px] sm:w-40 bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pendente</SelectItem>
                            <SelectItem value="in_progress">
                              Em Andamento
                            </SelectItem>
                            {!isPotentialTicket && <SelectItem value="resolved">Resolvido</SelectItem>}
                            {!isPotentialTicket && <SelectItem value="closed">Fechado</SelectItem>}
                          </SelectContent>
                        </Select>

                        <Dialog
                          open={showTransferDialog}
                          onOpenChange={setShowTransferDialog}
                        >
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="flex-1 sm:flex-none shadow-sm"
                            >
                              <UserPlus className="w-4 h-4 sm:mr-2 shrink-0" />
                              <span className="hidden sm:inline">
                                Transferir
                              </span>
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
                            <DialogHeader>
                              <DialogTitle className="text-xl sm:text-2xl">
                                Transferir Atendimento
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-2 w-full">
                              <div className="space-y-2">
                                <Label className="font-bold text-gray-700">
                                  Selecione o Atendente
                                </Label>
                                <Select
                                  value={selectedUser}
                                  onValueChange={setSelectedUser}
                                >
                                  <SelectTrigger className="bg-white w-full">
                                    <SelectValue placeholder="Selecione..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {users.map((u) => (
                                      <SelectItem key={u.id} value={u.id}>
                                        <span className="flex items-center gap-2">
                                          <span className="w-2 h-2 bg-green-500 rounded-full shrink-0"></span>
                                          <span className="truncate">
                                            {u.name}
                                          </span>
                                        </span>
                                      </SelectItem>
                                    ))}
                                    {users.length === 0 && (
                                      <SelectItem value="_none_" disabled>
                                        Nenhum atendente encontrado
                                      </SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label className="font-bold text-gray-700">
                                  Motivo (opcional)
                                </Label>
                                <Textarea
                                  value={transferReason}
                                  onChange={(e) =>
                                    setTransferReason(e.target.value)
                                  }
                                  placeholder="Informe o motivo da transferência..."
                                  rows={3}
                                  className="bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y w-full"
                                />
                              </div>

                              <div className="pt-2 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full">
                                <Button
                                  variant="outline"
                                  onClick={() => setShowTransferDialog(false)}
                                  className="w-full sm:w-auto"
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  onClick={handleTransfer}
                                  className="w-full sm:flex-1 bg-blue-600 hover:bg-blue-700 shadow-md"
                                >
                                  Confirmar Transferência
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}

                    <Button variant="ghost" size="icon" onClick={loadMessages}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <Dialog
              open={showDepartmentPrompt}
              onOpenChange={setShowDepartmentPrompt}
            >
              <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="text-xl sm:text-2xl">
                    Departamento Obrigatório
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-500 mt-1">
                    Este é um atendimento potencial. Selecione o departamento
                    antes de prosseguir com esta ação.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-2 space-y-4 w-full">
                  <div className="space-y-2">
                    <Label className="font-bold text-gray-700">
                      Selecione o Departamento
                    </Label>
                    <Select
                      value={requiredDepartment}
                      onValueChange={setRequiredDepartment}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map((d) => (
                          <SelectItem key={d.question} value={d.question}>
                            {d.question}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="pt-2 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full">
                  <Button
                    variant="outline"
                    onClick={() => setShowDepartmentPrompt(false)}
                    className="w-full sm:w-auto"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={confirmDepartmentAndProceed}
                    className="w-full sm:flex-1 bg-blue-600 hover:bg-blue-700 shadow-md"
                  >
                    Confirmar e Prosseguir
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog
              open={showResolveDialog}
              onOpenChange={setShowResolveDialog}
            >
              <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-xl sm:text-2xl">
                    <CheckCircle className="w-6 h-6 text-green-600 shrink-0" />
                    Finalizar Atendimento
                  </DialogTitle>
                  <DialogDescription>
                    {ticket.status === "pending" && !ticket.assigned_to
                      ? "Ao confirmar, você assumirá este atendimento da Fila e registrará a finalização em seu nome."
                      : "Registre a categoria e a solução para finalizar este atendimento."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2 w-full">
                  <div className="space-y-2">
                    <Label className="font-bold text-gray-700">
                      Categoria do Atendimento *
                    </Label>
                    <Select
                      value={resolveData.category}
                      onValueChange={(value) =>
                        setResolveData({ ...resolveData, category: value })
                      }
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Selecione a categoria..." />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-bold text-gray-700">
                      Descrição da Solução *
                    </Label>
                    <Textarea
                      value={resolveData.description}
                      onChange={(e) =>
                        setResolveData({
                          ...resolveData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Descreva como o problema foi resolvido..."
                      rows={4}
                      className="bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-bold text-gray-700">
                      Status Final
                    </Label>
                    <Select
                      value={resolveData.status}
                      onValueChange={(value) =>
                        setResolveData({ ...resolveData, status: value })
                      }
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="resolved">Resolvido</SelectItem>
                        <SelectItem value="closed">Fechado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="pt-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full">
                    <Button
                      variant="outline"
                      onClick={() => setShowResolveDialog(false)}
                      className="w-full sm:w-auto"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleResolveTicket}
                      disabled={isResolving}
                      className="w-full sm:flex-1 bg-green-600 hover:bg-green-700 shadow-md"
                    >
                      {isResolving ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2 shrink-0" />
                          Finalizando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2 shrink-0" />
                          Finalizar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <ClientNameChoiceDialog
              open={Boolean(pendingClientSelection)}
              onOpenChange={(open) => { if (!open) setPendingClientSelection(null); }}
              currentName={pendingClientSelection?.currentName}
              companyName={pendingClientSelection?.companyName}
              onConfirm={(updateClientName) => {
                applySelectedClient(pendingClientSelection.clientData, updateClientName);
                setPendingClientSelection(null);
              }}
            />

            <Dialog
              open={showEditClientDialog}
              onOpenChange={setShowEditClientDialog}
            >
              <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[95dvh] p-4 sm:p-6 overflow-y-auto overflow-x-hidden rounded-xl sm:rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-xl sm:text-2xl">
                    <Edit3 className="w-6 h-6 text-blue-600 shrink-0" />
                    Editar Contato
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-2 w-full">
                  <div className="space-y-2">
                    <Label className="font-bold text-gray-700">
                      Nome do Cliente
                    </Label>
                    <Input
                      value={clientEditData.name}
                      onChange={(e) =>
                        setClientEditData({
                          ...clientEditData,
                          name: e.target.value,
                        })
                      }
                      placeholder="Ex: Fernando Silva"
                      className="bg-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="font-bold text-gray-700">
                      Número Real (WhatsApp)
                    </Label>
                    <Input
                      value={clientEditData.real_phone}
                      onChange={(e) =>
                        setClientEditData({
                          ...clientEditData,
                          real_phone: e.target.value,
                        })
                      }
                      placeholder="Ex: 5511999999999"
                      className="bg-white"
                    />
                    <p className="text-[11px] sm:text-xs text-gray-500 leading-tight">
                      Se o cliente apareceu com um "ID Oculto", pergunte o
                      número real e anote aqui.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center justify-between font-bold text-gray-700">
                      <span>Empresa / Razão Social</span>
                      {loadingClients && (
                        <Loader2 className="w-3 h-3 animate-spin text-purple-600" />
                      )}
                    </Label>
                    <Input
                      value={clientEditData.company}
                      onChange={(e) =>
                        setClientEditData({
                          ...clientEditData,
                          company: e.target.value,
                        })
                      }
                      placeholder="Digite manualmente ou busque abaixo..."
                      className="border-purple-200 focus:border-purple-500 focus:ring-purple-500 bg-white"
                    />
                  </div>

                  <div className="bg-gray-100 p-2.5 rounded-lg border border-gray-200 w-full relative">
                    <div className="space-y-2 w-full relative">
                      <div className="relative w-full">
                        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <Input
                          placeholder="Buscar empresa (CNPJ, Razão...)"
                          value={clientSearch}
                          onChange={(e) => setClientSearch(e.target.value)}
                          className="pl-8 h-9 text-xs sm:text-sm bg-white w-full"
                        />
                      </div>

                      {clientSearch && (
                        <div className="absolute z-50 left-0 right-0 w-full mt-1 max-h-40 overflow-y-auto bg-white border border-purple-200 rounded-md shadow-lg divide-y divide-gray-100 custom-scrollbar">
                          {filteredClients.length === 0 ? (
                            <div className="p-3 text-center text-xs text-gray-500">
                              Nenhuma empresa encontrada
                            </div>
                          ) : (
                            filteredClients.slice(0, 15).map((client) => (
                              <div
                                key={client.id}
                                onClick={() =>
                                  handleSelectClientFromApi(client)
                                }
                                className="p-2 text-xs hover:bg-purple-50 cursor-pointer flex flex-col gap-0.5 transition-colors"
                              >
                                <span className="font-semibold text-gray-800 truncate">
                                  {client.name ||
                                    client.razao_social ||
                                    client.nome_fantasia ||
                                    "Sem Nome"}
                                </span>
                                {client.cnpj && (
                                  <span className="text-[11px] text-gray-500 shrink-0">
                                    CNPJ: {client.cnpj}
                                  </span>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-4 flex flex-col-reverse sm:flex-row gap-2 w-full">
                    <Button
                      variant="destructive"
                      type="button"
                      onClick={() => {
                        setClientEditData({
                          real_phone: "",
                          company: "",
                          external_client_id: "",
                        });
                        setClientSearch("");
                      }}
                      className="w-full sm:w-auto sm:mr-auto px-4"
                      title="Limpar todos os campos e desvincular"
                    >
                      Limpar
                    </Button>
                    <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
                      <Button
                        variant="outline"
                        onClick={() => setShowEditClientDialog(false)}
                        className="w-full sm:w-auto"
                      >
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleSaveClient}
                        disabled={isSavingClient}
                        className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 shadow-md"
                      >
                        {isSavingClient ? "Salvando..." : "Salvar Contato"}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <div
              className={`transition-all duration-300 ease-in-out origin-top overflow-hidden ${headerExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}
            >
              {ticket.client_identified &&
                (ticket.external_client_company ||
                  ticket.external_client_name) && (
                  <div className="max-w-3xl mx-auto flex flex-col items-start justify-between gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white p-3 rounded-b-md mb-2 shadow-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold flex items-center gap-1.5">
                        🔗 Cliente Identificado
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center sm:gap-4 text-sm text-purple-50">
                      <span className="flex items-center gap-1.5 font-medium">
                        🏢{" "}
                        {ticket.external_client_company ||
                          ticket.external_client_name}
                      </span>

                      {ticket.external_client_data?.cnpj && (
                        <span className="flex items-center gap-1.5 font-medium">
                          📄 {formatCNPJ(ticket.external_client_data?.cnpj)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

              {(ticket.initial_message ||
                ticket.selected_menu_option ||
                ticket.assigned_to_name) && (
                <div className="bg-gray-300 border-b px-4 sm:px-6 py-4">
                  <div className="max-w-6xl mx-auto">
                    <div className="flex items-start gap-4">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {ticket.initial_message && (
                          <div className="bg-white p-2.5 sm:p-3 rounded-lg shadow-sm border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              📝 Motivo do Contato
                            </p>
                            <p className="text-sm text-gray-900 font-medium">
                              "{ticket.initial_message}"
                            </p>
                          </div>
                        )}

                        {ticket.selected_menu_option ? (
                          <div className="bg-white p-2.5 sm:p-3 rounded-lg shadow-sm border-l-4 border-green-500">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              ✅ Opção Selecionada
                            </p>
                            <p className="text-sm text-green-700 font-bold">
                              {ticket.selected_menu_option}
                            </p>
                          </div>
                        ) : ticket.initial_message ? (
                          <div className="bg-yellow-50 p-2.5 sm:p-3 rounded-lg shadow-sm border-l-4 border-yellow-500">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              ⏳ Aguardando Seleção
                            </p>
                            <p className="text-sm text-yellow-700 font-medium">
                              Cliente ainda não selecionou uma opção do menu
                            </p>
                          </div>
                        ) : null}

                        <div className="bg-blue-50 p-2.5 sm:p-3 rounded-lg shadow-sm border-l-4 border-blue-500">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                            <UserIcon className="w-3.5 h-3.5" /> Responsável
                          </p>
                          <p className="text-sm text-blue-800 font-bold flex items-center gap-2">
                            {ticket.assigned_to_name ? (
                              <>
                                {ticket.assigned_to_name}
                                {user?.id === ticket.assigned_to && (
                                  <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full font-normal">
                                    Você
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-500 italic">
                                Não atribuído
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              className="flex-1 overflow-y-auto bg-[#efeae2]"
              ref={chatContainerRef}
              onScroll={handleScroll}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isDragging && (
                <div className="absolute inset-0 z-[100] bg-blue-500/80 backdrop-blur-sm flex flex-col items-center justify-center rounded-lg m-2 border-4 border-dashed border-white">
                  <div className="bg-white p-4 rounded-full mb-4 animate-bounce">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                  </div>
                  <h2 className="text-3xl font-black text-white tracking-wider drop-shadow-md">
                    SOLTE O ARQUIVO AQUI
                  </h2>
                  <p className="text-blue-100 mt-2 font-medium">
                    O arquivo será enviado para{" "}
                    {ticket?.client_name || "o cliente"}
                  </p>
                </div>
              )}

              {hasMoreMessages && (
                <div className="flex justify-center my-4 w-full">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLoadMoreMessages}
                    className="bg-white text-blue-600 border-blue-200 hover:bg-blue-50 shadow-sm rounded-full px-6"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Carregar mensagens anteriores
                  </Button>
                </div>
              )}

              <div className="max-w-full px-4 flex flex-col justify-center my-1 w-full">
                {memoizedMessages}
              </div>
            </div>

            {isScrolledUp && unreadInChat > 0 && (
              <button
                onClick={() => scrollToBottom(false, true)}
                className="absolute bottom-[90px] right-6 z-50 flex items-center justify-center w-10 h-10 bg-white border border-gray-200 shadow-xl rounded-full text-gray-500 hover:text-blue-600 transition-all animate-in slide-in-from-bottom-5 hover:scale-110"
              >
                <ChevronDown className="w-6 h-6" />
                <span className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm border-2 border-white">
                  {unreadInChat}
                </span>
              </button>
            )}

            {canReply && (
              <div className="bg-gray-200/80 border-t p-3 relative backdrop-blur-md z-20">
                {replyingTo && (
                  <div className="w-full lg:max-w-6xl xl:max-w-7xl mx-auto mb-2 flex items-center justify-between bg-gray-100 border-l-4 border-blue-500 p-2.5 rounded-lg shadow-sm animate-in fade-in slide-in-from-bottom-2">
                    <div className="flex flex-col truncate pr-4">
                      <span className="font-bold text-blue-600 text-xs tracking-wider mb-0.5 uppercase">
                        Respondendo a: {replyingTo.sender}
                      </span>
                      <span className="text-gray-600 text-sm truncate max-w-[300px] sm:max-w-md">
                        {replyingTo.message || "📎 Arquivo/Anexo"}
                      </span>
                    </div>
                    <button
                      onClick={() => setReplyingTo(null)}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <div className="w-full lg:max-w-6xl xl:max-w-7xl mx-auto relative flex items-center gap-2">
                  {showQuickMenu && !isRecording && !recordedAudio && !isPreparingAudio && (
                    <div className="absolute bottom-full left-28 mb-2 w-[calc(100%-60px)] max-w-sm bg-white border shadow-xl rounded-lg overflow-hidden z-50">
                      <div className="bg-gray-100 px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center border-b">
                        <Zap className="w-4 h-4 mr-1 text-yellow-500" />{" "}
                        Respostas Rápidas
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        {filteredReplies.map((qr) => (
                          <div
                            key={qr.id}
                            className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-0 transition-colors"
                            onClick={() => applyQuickReply(qr.response)}
                          >
                            <div className="font-bold text-blue-600 text-sm mb-1">
                              {qr.trigger}
                            </div>
                            <div className="text-gray-600 text-xs truncate">
                              {qr.response}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isRecording || recordedAudio || isPreparingAudio ? (
                    <VoiceRecordingControls
                      isRecording={isRecording}
                      isPaused={isPaused}
                      isPreparing={isPreparingAudio}
                      isSending={sendingAudio}
                      audioUrl={recordedAudio?.url}
                      duration={recordingTime}
                      stream={mediaRecorderRef.current?.stream}
                      audioRef={audioPreviewRef}
                      onPause={handlePauseRecording}
                      onResume={handleResumeRecording}
                      onDiscard={handleCancelRecording}
                      onSend={handleSendRecordedAudio}
                      onPlaybackError={() => toast({
                        title: "Não foi possível reproduzir a prévia",
                        description: "Você ainda pode continuar a gravação ou enviar o áudio.",
                        variant: "destructive",
                      })}
                    />
                  ) : (
                    <>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={
                          sendingFile ||
                          sendingAudio ||
                          sendingMessage ||
                          ticket?.status !== "in_progress"
                        }
                        className="text-gray-500 hover:bg-gray-300 hover:text-gray-800 shrink-0 h-11 w-11 rounded-full transition-colors"
                      >
                        {sendingFile ? (
                          <div className="animate-spin w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full" />
                        ) : (
                          <Paperclip className="w-5 h-5" />
                        )}
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowProcedurePicker(true)}
                        disabled={
                          sendingFile ||
                          sendingAudio ||
                          sendingMessage ||
                          ticket?.status !== "in_progress" ||
                          !canReply
                        }
                        className="text-gray-500 hover:bg-gray-300 hover:text-violet-700 shrink-0 h-11 w-11 rounded-full transition-colors"
                        title="Enviar procedimento gravado"
                        aria-label="Enviar procedimento gravado"
                      >
                        <FileVideo className="w-5 h-5" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleStartRecording}
                        title="Gravar áudio"
                        aria-label="Gravar áudio"
                        disabled={
                          isStartingRecording ||
                          sendingFile ||
                          sendingAudio ||
                          sendingMessage ||
                          ticket?.status !== "in_progress"
                        }
                        className="text-gray-500 hover:bg-gray-300 hover:text-gray-800 shrink-0 h-11 w-11 rounded-full transition-colors"
                      >
                        {sendingAudio || isStartingRecording ? (
                          <div className="animate-spin w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full" />
                        ) : (
                          <Mic className="w-5 h-5" />
                        )}
                      </Button>

                      <Textarea
                        ref={messageInputRef}
                        placeholder={
                          ticket?.status !== "in_progress"
                            ? "Assuma o atendimento para poder responder..."
                            : "Digite sua mensagem (use '/' para respostas rápidas)"
                        }
                        value={newMessage}
                        onChange={handleMessageChange}
                        onPaste={handleInputPaste}
                        disabled={
                          sendingFile ||
                          sendingAudio ||
                          sendingMessage ||
                          ticket?.status !== "in_progress"
                        }
                        rows={1}
                        className="flex-1 border-none shadow-sm focus-visible:ring-0 bg-white rounded-2xl px-5 py-4 text-base resize-none overflow-y-auto min-h-[56px] leading-relaxed"
                        style={{ maxHeight: "120px" }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Tab" &&
                            !e.shiftKey &&
                            showQuickMenu &&
                            filteredReplies.length === 1
                          ) {
                            e.preventDefault();
                            applyQuickReply(filteredReplies[0].response);
                            return;
                          }

                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();

                            if (
                              !sendingFile &&
                              !sendingAudio &&
                              !sendingMessage &&
                              ticket?.status === "in_progress"
                            ) {
                              handleSendMessage();
                            }
                          }
                        }}
                      />

                      {newMessage.trim() && (
                        <Button
                          onClick={handleSendMessage}
                          disabled={
                            sendingFile ||
                            sendingAudio ||
                            sendingMessage ||
                            ticket?.status !== "in_progress"
                          }
                          size="icon"
                          className="shrink-0 bg-green-600 hover:bg-green-700 text-white rounded-full h-10 w-10 transition-transform hover:scale-105"
                        >
                          {sendingMessage ? (
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {isSpectator && (
              <div className="bg-blue-50/95 border-t border-blue-200 p-4 text-center z-20 backdrop-blur-md shadow-inner flex flex-col items-center">
                <div className="text-blue-700 font-bold flex items-start sm:items-center justify-center gap-2 text-sm sm:text-base text-left sm:text-center">
                  <Eye className="w-5 h-5 shrink-0 animate-pulse mt-0.5 sm:mt-0" />
                  <span>
                    Modo Espectador: Acompanhando o atendimento de{" "}
                    {ticket.assigned_to_name}.
                  </span>
                </div>
                <p className="text-blue-500 text-xs font-medium mt-1.5">
                  Você pode ler o histórico, mas o envio de mensagens está
                  desabilitado.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {isProfilePicOpen && ticket.client_pfp_url && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8 animate-in fade-in duration-200"
          onClick={() => setIsProfilePicOpen(false)}
        >
          <button
            onClick={() => setIsProfilePicOpen(false)}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full p-2 transition-all z-10"
            title="Fechar"
          >
            <X className="w-6 h-6 sm:w-8 sm:h-8" />
          </button>

          <img
            src={ticket.client_pfp_url}
            alt={ticket.client_name}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[85vh] aspect-square object-cover rounded-full sm:rounded-3xl shadow-2xl select-none ring-4 ring-white/10"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://api.dicebear.com/8.x/initials/svg?seed=Generico";
            }}
          />
        </div>
      )}

      {zoomedImage && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 backdrop-blur-sm animate-in fade-in duration-200">
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={5}
            centerOnInit={true}
            limitToBounds={true}
            wheel={{ step: 0.005 }}
            pinch={{ step: 5 }}
          >
            {({ zoomIn, zoomOut, resetTransform, state }) => (
              <React.Fragment>
                <div className="absolute top-0 inset-x-0 flex items-center justify-between p-4 sm:p-6 z-10 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
                  <div className="flex items-center gap-2 bg-black/50 rounded-full px-3 py-1.5 backdrop-blur-md border border-white/20 text-white pointer-events-auto shadow-lg">
                    <button
                      onClick={() => zoomOut()}
                      className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                      title="Diminuir"
                    >
                      <ZoomOut className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>

                    <span className="font-mono font-medium w-12 text-center text-sm sm:text-base">
                      {Math.round((state?.scale || 1) * 100)}%
                    </span>

                    <button
                      onClick={() => zoomIn()}
                      className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                      title="Aumentar"
                    >
                      <ZoomIn className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>

                    <div className="w-px h-5 bg-white/20 mx-1"></div>

                    <button
                      onClick={(e) =>
                        handleDownload(e, zoomedImage, "anexo_imagem.jpg")
                      }
                      className="p-1.5 hover:bg-white/20 rounded-full transition-colors text-blue-400"
                      title="Baixar Imagem"
                    >
                      <Download className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      setZoomedImage(null);
                      resetTransform();
                    }}
                    className="p-2 hover:bg-white/20 rounded-full text-white transition-colors bg-black/50 backdrop-blur-md border border-white/20 pointer-events-auto shadow-lg"
                    title="Fechar (Esc)"
                  >
                    <X className="w-6 h-6 sm:w-8 sm:h-8" />
                  </button>
                </div>

                <div className="flex-1 w-full h-full flex items-center justify-center overflow-hidden">
                  <TransformComponent
                    wrapperStyle={{ width: "100%", height: "100%" }}
                    contentStyle={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={zoomedImage}
                      alt="Zoom"
                      className="max-w-full max-h-[100vh] object-contain shadow-2xl cursor-grab active:cursor-grabbing"
                    />
                  </TransformComponent>
                </div>
              </React.Fragment>
            )}
          </TransformWrapper>
        </div>
      )}
    </div>
  );
};

export default Chat;
